import { useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectsApi, scheduleApi, type Activity, type ScheduleResponse } from '@/lib/apiClient';
import { StateView, Badge, Modal, Button } from '@/components';
import { fmtDate, parseLocalDate } from '@/lib/dates';
import { fetchLatestScheduleXml, GraphConsentError } from '@/lib/graphUpload';
import { useCanEditProject } from './OverviewEdit';
import { outlineRows, type OutlineRow } from './lib/scheduleOutline';

// Delegated Graph scope, acquired ON DEMAND (NOT part of loginRequest).
// Files.ReadWrite.All also grants read — same scope the upload path uses.
const GRAPH_SCOPES = ['Files.ReadWrite.All'];

// ─── Date / geometry helpers ─────────────────────────────────────────────────

function todayLocal(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

// "May 21 – Jun 4, 2026" style range. Falls back to a single date or em dash.
function dateRange(start: string | null, finish: string | null): string {
  if (start && finish) return `${fmtDate(start)} – ${fmtDate(finish)}`;
  if (start) return fmtDate(start);
  if (finish) return fmtDate(finish);
  return '—';
}

// Whole-day delta between two local calendar dates (b - a).
function dayDelta(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

// ─── 3-week look-ahead bucketing ─────────────────────────────────────────────
// An activity is "in the look-ahead" when it starts within the next 21 days OR
// is already in progress (started, not finished) as of today. Each is labeled
// by which of the next three 7-day weeks its start falls in (in-progress items
// that already started land in "This week").
type WeekKey = 'week1' | 'week2' | 'week3';
const WEEK_LABELS: Record<WeekKey, string> = {
  week1: 'This week',
  week2: 'Next week',
  week3: 'In 2 weeks',
};

function lookaheadWeek(act: Activity, today: Date): WeekKey | null {
  const s = parseLocalDate(act.start);
  const f = parseLocalDate(act.finish);
  // In progress as of today → This week.
  if (s && f && s.getTime() <= today.getTime() && f.getTime() >= today.getTime()) {
    return 'week1';
  }
  if (!s) return null;
  const days = dayDelta(today, s);
  if (days < 0 || days > 20) return null;
  if (days <= 6) return 'week1';
  if (days <= 13) return 'week2';
  return 'week3';
}

// ─── Mini-Gantt geometry ─────────────────────────────────────────────────────
// Sort non-summary activities by start (then finish, then uid). Activities with
// no start sort to the end so the dated ones drive the timeline. Used for the
// 3-week look-ahead; the chart list keeps MS Project's own outline order.
function sortByStart(a: Activity, b: Activity): number {
  const as = parseLocalDate(a.start)?.getTime();
  const bs = parseLocalDate(b.start)?.getTime();
  if (as != null && bs != null && as !== bs) return as - bs;
  if (as == null && bs != null) return 1;
  if (as != null && bs == null) return -1;
  const af = parseLocalDate(a.finish)?.getTime() ?? Number.MAX_SAFE_INTEGER;
  const bf = parseLocalDate(b.finish)?.getTime() ?? Number.MAX_SAFE_INTEGER;
  if (af !== bf) return af - bf;
  return a.uid - b.uid;
}

interface GanttSpan {
  min: number; // ms of earliest start
  max: number; // ms of latest finish
  span: number; // max - min, always > 0
}

// Overall [min start, max finish] across the dated activities. Returns null when
// nothing has usable dates; guards a zero/negative span up to a single day.
function computeSpan(activities: Activity[]): GanttSpan | null {
  let min = Infinity;
  let max = -Infinity;
  for (const a of activities) {
    const s = parseLocalDate(a.start)?.getTime();
    const f = parseLocalDate(a.finish)?.getTime();
    if (s != null) {
      min = Math.min(min, s);
      max = Math.max(max, s);
    }
    if (f != null) {
      min = Math.min(min, f);
      max = Math.max(max, f);
    }
  }
  if (!isFinite(min) || !isFinite(max)) return null;
  const span = Math.max(max - min, 86_400_000); // never less than one day
  return { min, max: min + span, span };
}

// Position of one activity's bar within the chart, as left%/width% (clamped to
// [0,100]). Bars get a small minimum width so single-day tasks stay visible.
const MIN_BAR_PCT = 1.5;
function barGeometry(
  a: Activity,
  { min, span }: GanttSpan,
): { leftPct: number; widthPct: number } | null {
  const s = parseLocalDate(a.start)?.getTime();
  const f = parseLocalDate(a.finish)?.getTime();
  if (s == null && f == null) return null;
  const startMs = s ?? f!;
  const finishMs = f ?? s!;
  const leftPct = ((startMs - min) / span) * 100;
  const rawWidth = ((finishMs - startMs) / span) * 100;
  const widthPct = Math.max(rawWidth, MIN_BAR_PCT);
  return {
    leftPct: Math.max(0, Math.min(100, leftPct)),
    widthPct: Math.max(MIN_BAR_PCT, Math.min(100 - Math.max(0, leftPct), widthPct)),
  };
}

// Fractional position (0–1) of today across the whole chart span, or null when
// today falls outside it. Drives the single TODAY line over the chart.
function todayFractionOfSpan({ min, span }: GanttSpan): number | null {
  const t = todayLocal().getTime();
  if (t < min || t > min + span) return null;
  return (t - min) / span;
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const css = `
  .sch-banner {
    display: flex; align-items: center; gap: var(--ew-s-3); flex-wrap: wrap;
    background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md);
    padding: 12px var(--ew-s-4); margin-bottom: var(--ew-s-4);
  }
  .sch-chip {
    width: 30px; height: 30px; border-radius: var(--ew-r-sm); flex: none;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--ew-quality); color: #fff;
    font: var(--ew-fw-semibold) 16px/1 var(--ew-font);
  }
  .sch-src-name { font: var(--ew-fw-semibold) var(--ew-fs-body) var(--ew-font); color: var(--ew-dark); }
  .sch-src-meta { font: var(--ew-fw-regular) 12px/1.3 var(--ew-font); color: var(--ew-n-500); }

  /* ── Mini-Gantt ── */
  /* Shared column template so the axis header lines up with each row. */
  .sch-gantt-axis,
  .sch-grow {
    display: grid; grid-template-columns: minmax(170px, 1.3fr) minmax(220px, 2.6fr) minmax(140px, max-content) 48px;
    gap: var(--ew-s-3); align-items: center;
  }
  .sch-gantt-axis {
    padding: 8px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-200);
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500);
  }
  .sch-axis-scale {
    display: flex; justify-content: space-between; align-items: baseline;
    color: var(--ew-n-500); font-variant-numeric: tabular-nums; text-transform: none;
    letter-spacing: 0; font-weight: var(--ew-fw-medium);
  }
  .sch-grow {
    padding: 9px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font);
  }
  .sch-grow:last-child { border-bottom: 0; }
  .sch-name {
    color: var(--ew-dark); font-weight: var(--ew-fw-medium);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .sch-dates { color: var(--ew-n-500); font-variant-numeric: tabular-nums; }
  .sch-pct { text-align: right; color: var(--ew-n-600); font-variant-numeric: tabular-nums; font-weight: var(--ew-fw-medium); }
  .sch-crit-dot { color: var(--ew-danger); margin-right: 6px; }

  /* Track spans the full timeline; the bar floats inside it. */
  .sch-track { position: relative; height: 16px; }
  .sch-track-line {
    position: absolute; left: 0; right: 0; top: 50%; height: 1px;
    background: var(--ew-n-100); transform: translateY(-50%);
  }
  .sch-gbar {
    position: absolute; top: 50%; transform: translateY(-50%); height: 12px;
    border-radius: var(--ew-r-pill); overflow: hidden;
    min-width: 3px;
  }
  /* Lighter remainder (whole bar), darker solid for the completed portion. */
  .sch-gbar-done { position: absolute; left: 0; top: 0; bottom: 0; border-radius: var(--ew-r-pill); }
  .sch-gbar.is-crit { background: color-mix(in srgb, var(--ew-danger) 28%, #fff); }
  .sch-gbar.is-crit .sch-gbar-done { background: var(--ew-danger); }
  .sch-gbar.is-normal { background: color-mix(in srgb, var(--ew-green) 26%, #fff); }
  .sch-gbar.is-normal .sch-gbar-done { background: var(--ew-green); }
  /* A summary task: the header over the tasks beneath it (#738). */
  .sch-grow.is-header { background: var(--ew-n-50); }
  .sch-grow.is-header .sch-name,
  .sch-grow.is-header .sch-pct { font-weight: var(--ew-fw-semibold); }
  .sch-grow.is-header .sch-dates { color: var(--ew-n-700); }
  .sch-gbar.is-summary { background: color-mix(in srgb, var(--ew-dark) 22%, #fff); }
  .sch-gbar.is-summary .sch-gbar-done { background: var(--ew-dark); }
  .sch-gbar-undated { background: repeating-linear-gradient(45deg, var(--ew-n-100), var(--ew-n-100) 4px, var(--ew-n-50) 4px, var(--ew-n-50) 8px); }

  /* One TODAY line across the whole chart column. */
  .sch-chart-col { position: relative; }
  .sch-today-line {
    position: absolute; top: 0; bottom: 0; width: 2px; background: var(--ew-dark);
    z-index: 2; pointer-events: none;
  }
  /* "TODAY" caption only on the first row's line (data-label), so it shows once. */
  .sch-today-line[data-label]::before {
    content: 'TODAY'; position: absolute; top: -15px; left: 50%; transform: translateX(-50%);
    font: var(--ew-fw-semibold) 9px/1 var(--ew-font); letter-spacing: 0.06em; color: var(--ew-dark);
    white-space: nowrap;
  }

  /* Look-ahead */
  .sch-week-hd {
    display: flex; align-items: baseline; gap: var(--ew-s-2);
    padding: 12px var(--ew-s-4) 6px;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500);
  }
  .sch-la-row {
    display: flex; justify-content: space-between; gap: var(--ew-s-4);
    padding: 8px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font);
  }
  .sch-la-row:last-child { border-bottom: 0; }
  .sch-la-row span:first-child { color: var(--ew-dark); }
  .sch-la-row span:last-child { color: var(--ew-n-500); white-space: nowrap; font-variant-numeric: tabular-nums; }

  /* Import modal — two option cards (Sync / Upload) */
  .sch-opts2 { display: grid; gap: var(--ew-s-3); }
  .sch-ocard {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: var(--ew-s-4);
    display: grid; gap: var(--ew-s-2);
  }
  .sch-ocard.is-primary { border-color: var(--ew-green); background: var(--ew-green-50); }
  .sch-ocard-hd { display: flex; align-items: center; gap: var(--ew-s-2); }
  .sch-ocard-title { font: var(--ew-fw-semibold) var(--ew-fs-body) var(--ew-font); color: var(--ew-dark); }
  .sch-drop {
    border: 1px dashed var(--ew-n-300); border-radius: var(--ew-r-md); background: var(--ew-n-50);
    padding: var(--ew-s-4); text-align: center; display: flex; flex-direction: column;
    align-items: center; gap: var(--ew-s-2);
  }
  .sch-tip { font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-500); }
  .sch-opt { display: flex; align-items: flex-start; gap: var(--ew-s-2); padding: 6px 0; }
  .sch-opt label { font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-dark); }
  .sch-opt-note { font: var(--ew-fw-regular) 11px/1.2 var(--ew-font); color: var(--ew-n-400); }
  .sch-file-pill {
    font: var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-dark);
    background: var(--ew-green-50); border-radius: var(--ew-r-pill); padding: 4px 12px;
  }
  .sch-found {
    font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-600);
  }
  .sch-found b { color: var(--ew-dark); }
  .sch-spinner {
    display: inline-block; width: 11px; height: 11px; border: 2px solid var(--ew-green);
    border-right-color: transparent; border-radius: 50%; animation: sch-spin 0.7s linear infinite;
    vertical-align: -1px; margin-right: 6px;
  }
  @keyframes sch-spin { to { transform: rotate(360deg); } }
  .sch-err {
    padding: 8px 12px; background: #FBE4DF; color: #8F2414; border-radius: var(--ew-r-md);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font);
  }
`;

// ─── Token acquisition (shared by banner Re-sync + modal Sync) ───────────────
// Acquire a Graph token on demand. Silent first; popup fallback when
// interaction is required; anything else → GraphConsentError (admin consent for
// the delegated scope likely still pending). Mirrors FileAttachments.
function useGraphToken(): () => Promise<string> {
  const { instance, accounts } = useMsal();
  return async () => {
    try {
      const r = await instance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account: accounts[0] });
      return r.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        const r = await instance.acquireTokenPopup({ scopes: GRAPH_SCOPES });
        return r.accessToken;
      }
      throw new GraphConsentError('Graph consent required');
    }
  };
}

// Map any sync error to a friendly message for the UI.
function friendlySyncError(err: unknown): string {
  if (err instanceof GraphConsentError) {
    return 'SharePoint access is still being set up (admin consent pending). Try again once it’s granted.';
  }
  return err instanceof Error ? err.message : 'Sync failed.';
}

export default function ScheduleTab({ projectId }: { projectId: number }) {
  const [importOpen, setImportOpen] = useState(false);

  const query = useQuery({
    queryKey: ['schedule', projectId],
    queryFn: () => scheduleApi.get(projectId),
  });

  // Project identity for the Job Folder link. Shares the workspace cache so it's
  // usually already warm; sync degrades gracefully when the folder isn't linked.
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () =>
      projectsApi.get(projectId) as Promise<{ jobNumber: string; title: string; jobFolderUrl: string | null }>,
    enabled: Number.isFinite(projectId),
    staleTime: 60_000,
  });
  const jobFolderUrl = projectQuery.data?.jobFolderUrl ?? null;

  return (
    <>
      <style>{css}</style>
      <StateView query={query} isEmpty={() => false} empty="No schedule imported yet.">
        {(data) => (
          <ScheduleBody
            projectId={projectId}
            data={data}
            jobFolderUrl={jobFolderUrl}
            importOpen={importOpen}
            onOpenImport={() => setImportOpen(true)}
            onCloseImport={() => setImportOpen(false)}
          />
        )}
      </StateView>
    </>
  );
}

function ScheduleBody({
  projectId,
  data,
  jobFolderUrl,
  importOpen,
  onOpenImport,
  onCloseImport,
}: {
  projectId: number;
  data: ScheduleResponse;
  jobFolderUrl: string | null;
  importOpen: boolean;
  onOpenImport: () => void;
  onCloseImport: () => void;
}) {
  // POST /projects/:id/schedule/import requires projects.edit. This tab had no
  // permission check at all, so Re-sync / Import rendered for every signed-in
  // user — and Re-sync fires the import immediately on click with no confirm,
  // then silently opens the import modal when it fails instead of surfacing the
  // 403. Reading is unaffected; only the write controls are gated.
  const canEdit = useCanEditProject();
  const qc = useQueryClient();
  const getToken = useGraphToken();
  const hasSchedule = !!data.lastImport || data.activities.length > 0;
  const today = todayLocal();

  // Banner "Re-sync" runs the Job-Folder sync directly; on any error it falls
  // back to opening the modal (where the user sees the message + Upload option).
  const [reSyncing, setReSyncing] = useState(false);
  const reSync = async () => {
    if (!jobFolderUrl) {
      onOpenImport();
      return;
    }
    setReSyncing(true);
    try {
      const { file } = await fetchLatestScheduleXml({ getToken, jobFolderUrl });
      await scheduleApi.import(projectId, file);
      qc.invalidateQueries({ queryKey: ['schedule', projectId] });
    } catch {
      // Surface details + fallback (Upload) inside the modal.
      onOpenImport();
    } finally {
      setReSyncing(false);
    }
  };

  // Detail activities (non-summary), by start date → the 3-week look-ahead. A
  // header is a heading over tasks, not a task, so it is never listed there.
  const activities = useMemo(
    () => data.activities.filter((a) => !a.isSummary).slice().sort(sortByStart),
    [data.activities],
  );
  // The mini-Gantt's rows: MS Project's outline order, with its summary tasks as
  // headers over the tasks beneath them (#738).
  const rows = useMemo(() => outlineRows(data.activities), [data.activities]);
  // The span covers every row drawn, headers included. A header normally spans
  // exactly its tasks, but a manually scheduled one can carry its own dates, and
  // a bar that falls outside the chart would be clamped to its edge.
  const span = useMemo(() => computeSpan(rows.map((r) => r.activity)), [rows]);
  const todayFrac = span ? todayFractionOfSpan(span) : null;

  // 3-week look-ahead buckets.
  const buckets: Record<WeekKey, Activity[]> = { week1: [], week2: [], week3: [] };
  for (const a of activities) {
    const wk = lookaheadWeek(a, today);
    if (wk) buckets[wk].push(a);
  }
  const lookaheadCount = buckets.week1.length + buckets.week2.length + buckets.week3.length;

  return (
    <>
      {!hasSchedule ? (
        <EmptyState onImport={onOpenImport} canEdit={canEdit} />
      ) : (
        <>
          {/* ── System-of-record banner ── */}
          <div className="sch-banner">
            <span className="sch-chip" aria-hidden="true">P</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-2)', flexWrap: 'wrap' }}>
              <span className="sch-src-name">Microsoft Project</span>
              <Badge label="System of record" tone="info" />
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div className="sch-src-meta">
                {data.lastImport?.fileName ?? 'Imported schedule'}
                {data.lastImport ? ` · last imported ${fmtDate(data.lastImport.importedAt)}` : ''}
              </div>
            </div>
            {canEdit && (
              <div style={{ display: 'flex', gap: 'var(--ew-s-2)' }}>
                <Button variant="secondary" onClick={reSync} isLoading={reSyncing}>Re-sync</Button>
                <Button variant="primary" onClick={onOpenImport}>Import Schedule</Button>
              </div>
            )}
          </div>

          {/* ── Critical path / activities (mini-Gantt) ── */}
          <div className="pw-card">
            <h2 className="pw-card-hd">Critical path &amp; activities</h2>
            {activities.length === 0 ? (
              <p style={{ padding: 'var(--ew-s-4)', color: 'var(--ew-n-400)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', textAlign: 'center' }}>
                No detail activities in this schedule.
              </p>
            ) : (
              <Gantt rows={rows} span={span} todayFrac={todayFrac} />
            )}
          </div>

          {/* ── 3-week look-ahead ── */}
          <div className="pw-card" style={{ marginTop: 'var(--ew-s-4)' }}>
            <h2 className="pw-card-hd">3-week look-ahead</h2>
            {lookaheadCount === 0 ? (
              <p style={{ padding: 'var(--ew-s-4)', color: 'var(--ew-n-400)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', textAlign: 'center' }}>
                Nothing scheduled to start or in progress in the next 21 days.
              </p>
            ) : (
              (Object.keys(WEEK_LABELS) as WeekKey[]).map((wk) => {
                const rows = buckets[wk];
                if (rows.length === 0) return null;
                return (
                  <div key={wk}>
                    <div className="sch-week-hd">
                      {WEEK_LABELS[wk]}
                      <span style={{ color: 'var(--ew-n-400)', fontWeight: 400 }}>· {rows.length}</span>
                    </div>
                    {rows.map((a) => (
                      <div key={a.id} className="sch-la-row">
                        <span>
                          {a.isCritical ? (
                            <span className="sch-crit-dot" title="Critical path">●</span>
                          ) : null}
                          {a.name}
                        </span>
                        <span>{dateRange(a.start, a.finish)}</span>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {canEdit && (
        <ImportModal
          projectId={projectId}
          jobFolderUrl={jobFolderUrl}
          getToken={getToken}
          isOpen={importOpen}
          onClose={onCloseImport}
        />
      )}
    </>
  );
}

// ─── Mini-Gantt: axis header + one row per outline row ───────────────────────
function Gantt({
  rows,
  span,
  todayFrac,
}: {
  rows: OutlineRow[];
  span: GanttSpan | null;
  todayFrac: number | null;
}) {
  return (
    <div>
      {/* Date axis header (min … max) over the bar column for scale. */}
      <div className="sch-gantt-axis">
        <span>Activity</span>
        <span className="sch-axis-scale">
          {span ? (
            <>
              <span>{fmtDate(new Date(span.min).toISOString())}</span>
              <span>{fmtDate(new Date(span.max).toISOString())}</span>
            </>
          ) : (
            <span>Timeline</span>
          )}
        </span>
        <span style={{ textTransform: 'none', letterSpacing: 0 }}>Dates</span>
        <span style={{ textAlign: 'right' }}>%</span>
      </div>

      {/* Rows. The chart column carries the single TODAY line across all rows
          via an overlay; here we draw the line per-row so it spans the stack. */}
      {rows.map((row, i) => (
        <GanttRow
          key={row.activity.id}
          row={row}
          span={span}
          todayFrac={todayFrac}
          showTodayLabel={i === 0}
        />
      ))}
    </div>
  );
}

// Indent per outline level, in px, applied to the name only so the bars and the
// date and % columns stay lined up down the page. Capped, because the name column
// is only guaranteed 170px wide and a schedule can nest eight levels deep: past
// the cap, deeper rows line up with it rather than squeezing the name to nothing.
const INDENT_PX = 16;
const MAX_INDENT_LEVELS = 4;

function GanttRow({
  row,
  span,
  todayFrac,
  showTodayLabel,
}: {
  row: OutlineRow;
  span: GanttSpan | null;
  todayFrac: number | null;
  showTodayLabel: boolean;
}) {
  const { activity: a, isHeader, depth } = row;
  const pct = Math.max(0, Math.min(100, a.pctComplete ?? 0));
  const bar = span ? barGeometry(a, span) : null;
  // A header's "critical" flag only means something under it is, which would put
  // a red mark on nearly every header, so a header gets its own neutral bar and
  // no critical dot. The critical tasks beneath it carry them.
  const critClass = isHeader ? 'is-summary' : a.isCritical ? 'is-crit' : 'is-normal';

  return (
    <div className={isHeader ? 'sch-grow is-header' : 'sch-grow'} data-header={isHeader ? 'true' : undefined}>
      <span className="sch-name" title={a.name} style={{ paddingLeft: Math.min(depth, MAX_INDENT_LEVELS) * INDENT_PX }}>
        {a.isCritical && !isHeader ? (
          <span className="sch-crit-dot" title="Critical path">●</span>
        ) : null}
        {a.name || `Task ${a.uid}`}
      </span>

      {/* Timeline track */}
      <div className="sch-track sch-chart-col">
        <span className="sch-track-line" />
        {bar ? (
          <div
            className={`sch-gbar ${critClass}`}
            style={{ left: `${bar.leftPct}%`, width: `${bar.widthPct}%` }}
            title={`${dateRange(a.start, a.finish)} · ${pct}% complete${a.isCritical && !isHeader ? ' · critical path' : ''}`}
          >
            <span className="sch-gbar-done" style={{ width: `${pct}%` }} />
          </div>
        ) : (
          <div
            className="sch-gbar sch-gbar-undated"
            style={{ left: '0%', width: `${MIN_BAR_PCT}%` }}
            title="No dates on this activity"
          />
        )}
        {todayFrac != null ? (
          <span
            className="sch-today-line"
            style={{ left: `${todayFrac * 100}%` }}
            title="Today"
            data-label={showTodayLabel ? 'true' : undefined}
          />
        ) : null}
      </div>

      <span className="sch-dates">{dateRange(a.start, a.finish)}</span>
      <span className="sch-pct">{pct}%</span>
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────
function EmptyState({ onImport, canEdit }: { onImport: () => void; canEdit: boolean }) {
  return (
    <div className="pw-card" style={{ padding: 'var(--ew-s-8) var(--ew-s-5)', textAlign: 'center' }}>
      <div style={{ font: 'var(--ew-fw-semibold) 18px/1.3 var(--ew-font)', color: 'var(--ew-dark)', marginBottom: 'var(--ew-s-2)' }}>
        No schedule imported yet
      </div>
      <p style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm)/1.5 var(--ew-font)', color: 'var(--ew-n-500)', maxWidth: 480, margin: '0 auto var(--ew-s-4)' }}>
        Import an MS Project schedule (MSPDI XML) — or Sync from this job's SharePoint
        Folder, where we'll pull the newest <b>File ▸ Save As ▸ XML</b> export. See the
        critical path and a 3-week look-ahead here; MS Project stays the system of record.
      </p>
      {canEdit ? (
        <Button variant="primary" onClick={onImport}>Import or Sync schedule</Button>
      ) : (
        <p className="sch-src-meta">No schedule has been imported for this job yet.</p>
      )}
    </div>
  );
}

// ─── Import modal ────────────────────────────────────────────────────────────
// Two options: Sync from Job Folder (primary) + Upload file (manual fallback).
function ImportModal({
  projectId,
  jobFolderUrl,
  getToken,
  isOpen,
  onClose,
}: {
  projectId: number;
  jobFolderUrl: string | null;
  getToken: () => Promise<string>;
  isOpen: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [detectCritical, setDetectCritical] = useState(true);
  const [importBaseline, setImportBaseline] = useState(true);
  const [syncError, setSyncError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const reset = () => {
    setFile(null);
    setDetectCritical(true);
    setImportBaseline(true);
    setSyncError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const close = () => {
    reset();
    onClose();
  };

  // Manual upload (fallback).
  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Choose an MSPDI XML file to import.');
      return scheduleApi.import(projectId, file);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', projectId] });
      close();
    },
  });

  // Sync from Job Folder: pull the newest .xml → import → invalidate → close.
  const syncMutation = useMutation({
    mutationFn: async () => {
      if (!jobFolderUrl) throw new Error('No Job Folder is linked.');
      const { file: fetched } = await fetchLatestScheduleXml({ getToken, jobFolderUrl });
      await scheduleApi.import(projectId, fetched);
      return fetched.name;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', projectId] });
      close();
    },
    onError: (err) => setSyncError(friendlySyncError(err)),
  });

  const runSync = () => {
    setSyncError(null);
    syncMutation.mutate();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Import schedule"
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-3)', width: '100%' }}>
          <span className="sch-tip" style={{ flex: 1, textAlign: 'left' }}>
            Replaces the current schedule · MS Project stays system of record
          </span>
          <Button variant="secondary" onClick={close}>Close</Button>
        </div>
      }
    >
      <div className="sch-opts2">
        {/* ── Option 1: Sync from Job Folder (primary) ── */}
        <div className="sch-ocard is-primary">
          <div className="sch-ocard-hd">
            <span className="sch-ocard-title">Sync from Job Folder</span>
            <Badge label="Recommended" tone="info" />
          </div>
          <p className="sch-tip">
            Save an MS Project <b>File ▸ Save As ▸ XML</b> export into this job's
            SharePoint Folder; we'll pull the newest one.
          </p>

          {!jobFolderUrl ? (
            <p className="sch-tip" style={{ color: 'var(--ew-n-600)' }}>
              Link this project's Job Folder first (Projects → the project's Overview).
            </p>
          ) : (
            <>
              {syncError && <div className="sch-err">{syncError}</div>}
              <div>
                <Button variant="primary" onClick={runSync} isLoading={syncMutation.isPending}>
                  {syncMutation.isPending
                    ? <><span className="sch-spinner" aria-hidden="true" />Syncing…</>
                    : 'Sync now'}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* ── Option 2: Upload file (manual fallback) ── */}
        <div className="sch-ocard">
          <div className="sch-ocard-hd">
            <span className="sch-ocard-title">Upload file</span>
          </div>

          {uploadMutation.isError && (
            <div className="sch-err">{(uploadMutation.error as Error).message}</div>
          )}

          <div className="sch-drop">
            {file ? (
              <span className="sch-file-pill">{file.name}</span>
            ) : (
              <span className="sch-tip">No file chosen</span>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <span className="sch-tip">
              In MS Project: <b>File ▸ Save As ▸ XML</b> (MSPDI). <code>.mpp</code> files
              can't be read directly — export XML first.
            </span>
          </div>

          <div>
            <p style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)', margin: '0 0 var(--ew-s-2)' }}>
              Import options
            </p>
            <div className="sch-opt">
              <input id="sch-opt-crit" type="checkbox" checked={detectCritical} onChange={(e) => setDetectCritical(e.target.checked)} />
              <label htmlFor="sch-opt-crit">Detect critical path</label>
            </div>
            <div className="sch-opt">
              <input id="sch-opt-base" type="checkbox" checked={importBaseline} onChange={(e) => setImportBaseline(e.target.checked)} />
              <label htmlFor="sch-opt-base">Import baseline</label>
            </div>
            <div className="sch-opt">
              <input id="sch-opt-res" type="checkbox" disabled />
              <label htmlFor="sch-opt-res" style={{ color: 'var(--ew-n-400)' }}>
                Map resource assignments to manpower
                <span className="sch-opt-note"> — coming later</span>
              </label>
            </div>
          </div>

          <div>
            <Button
              variant="secondary"
              onClick={() => uploadMutation.mutate()}
              disabled={!file}
              isLoading={uploadMutation.isPending}
            >
              Import file
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
